import { getCurrentUser } from "aws-amplify/auth";
import SentryWrap from "components/Sentry";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Modal } from "@mui/material";
import { has } from "lodash";
import COLOR from "utils/constants/color";
import { getRandomCotation } from "utils/methods/math";
import { Button, Modal as AntModal, Space, Typography, message } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  FolderOutlined,
  BookOutlined,
  TeamOutlined,
  TrophyOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  AppstoreOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import CotationLoader from "components/CotationLoader";
import BooksTable from "features/forms/admin/BooksTable";
import { handleCreateBook, handleListActiveInActiveBooks } from "store/books/thunks";
import { useUI } from "store/UI/selectors";
import { useHasPermission } from "utils/permissions";

const { Title } = Typography;

const CenterItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  flex: 1;
  overflow: auto;
  padding-right: 8px;
  @media (max-width: 992px) {
    padding-right: 0px;
  }
`;

const VideoContainer = styled.div`
  height: auto;
  flex: 1;
  justify-content: center;
`;

const Containermain = styled.div`
  padding: 16px 0;
  width: 100%;
  height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    padding: 12px 0;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 16px;
  padding: 0 24px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const MenuBar = styled.div`
  margin-bottom: 16px;
  flex-shrink: 0;
  padding: 12px 24px;
  background: ${COLOR.white || "#ffffff"};
  border-radius: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const TableSection = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${COLOR.white || "#ffffff"};
  border-radius: 0;
  padding: 16px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

type MenuButtonType = "primary" | undefined;

type MenuButton = {
  key: string;
  type?: MenuButtonType;
  icon: React.ReactNode;
  label: string;
  allowed: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
};

const EditBooksList = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [bookCreateLoader, setBookCreateLoader] = useState<boolean>(false);
  const cotationRef = useRef<any>(null);
  const { quotesData = [] } = useUI() as any;

  const hasInformate = useHasPermission("book_sections.informate");
  const hasResources = useHasPermission("book_sections.resources");
  const hasBalcon = useHasPermission("book_sections.balcon");
  const hasConectandome = useHasPermission("book_sections.conectandome");
  const hasComunidad = useHasPermission("book_sections.comunidad");
  const hasStories = useHasPermission("book_sections.stories");
  const has1Wayteacher = useHasPermission("book_sections.1wayteacher");
  const hasAP = useHasPermission("book_sections.ap");
  const hasApExams = useHasPermission("book_sections.ap_exams");
  const hasExams = useHasPermission("book_sections.exams");
  const hasAddBooks = useHasPermission("edit_books.add");
  const hasHowToVideos = useHasPermission("book_sections.how_to_videos");
  const hasBookGrouping = useHasPermission("book_sections.book_grouping");
  const hasSentenceStarter = useHasPermission("book_sections.sentence_starter");
  const hasRubric = useHasPermission("book_sections.rubric");

  useEffect(() => {
    if (!cotationRef.current && quotesData?.length) {
      cotationRef.current = getRandomCotation(quotesData);
    }
  }, [quotesData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getCurrentUser();
      } catch {
        navigate("/");
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(handleListActiveInActiveBooks());
    };

    fetchData();
  }, [dispatch]);

  const onAddBook = async (): Promise<void> => {
    AntModal.confirm({
      title: "Create New Book",
      content: "This will create a new book. Are you sure you want to proceed?",
      okText: "Yes, Create Book",
      cancelText: "Cancel",
      onOk: async () => {
        setBookCreateLoader(true);
        const cBook = await dispatch(handleCreateBook({create_new:true}));
        if(cBook&&!cBook.error){
          navigate(`/add-book?bookId=${cBook.id}`);
        }else{
          message.error(cBook.error);
          setBookCreateLoader(false);
        }
       
      },
      onCancel: () => {
        setBookCreateLoader(false);
      },
    });
  };

  const editResources = () => {
    navigate("/edit-resources");
  };

  const edit1Wayteacher = () => {
    navigate("/edit-1way-teacher");
  };

  const editSentenceStarter = () => {
    navigate("/sentence-starter");
  };

  const hotToVideos = () => {
    navigate("/edit-how-to-videos");
  };

  const editExam = () => {
    navigate("/edit-exams");
  };

  const editBookGroup = () => {
    navigate("/edit-book-grouping");
  };

  const editApExams = () => {
    navigate("/edit-ap-exams");
  };

  const editStories = () => {
    navigate("/edit-story");
  };

  const rubricHandler = () => {
    navigate("/rubric");
  };

  const editBalcon = () => {
    navigate(`/edit-book-units/?bookId=33`);
  };

  const editInformate = () => {
    navigate(`/edit-book-units/?bookId=41`);
  };

  const editComunidad = () => {
    navigate(`/edit-book-units/?bookId=43`);
  };

  const editConectándome = () => {
    navigate(`/edit-book-units/?bookId=49`);
  };

  const editAP = () => {
    navigate(`/edit-all-ap?id=69`);
  };

  const permissionWarningMessage = "You do not have permission to access this feature.";

  const menuButtons: MenuButton[] = [
    {
      key: "add-books",
      type: "primary",
      icon: <PlusOutlined />,
      label: "Add Books",
      allowed: hasAddBooks,
      onClick: () => {
        void onAddBook();
      },
      style: { fontWeight: 600 },
    },
    {
      key: "book-grouping",
      icon: <AppstoreOutlined />,
      label: "Book Grouping",
      allowed: hasBookGrouping,
      onClick: editBookGroup,
    },
    {
      key: "edit-informate",
      icon: <EditOutlined />,
      label: "Edit Informate",
      allowed: hasInformate,
      onClick: editInformate,
    },
    {
      key: "edit-balcon",
      icon: <BookOutlined />,
      label: "Edit Balcón cultural",
      allowed: hasBalcon,
      onClick: editBalcon,
    },
    {
      key: "edit-conectandome",
      icon: <BookOutlined />,
      label: "Edit Conectándome",
      allowed: hasConectandome,
      onClick: editConectándome,
    },
    {
      key: "edit-comunidad",
      icon: <BookOutlined />,
      label: "Edit Comunidad",
      allowed: hasComunidad,
      onClick: editComunidad,
    },
    {
      key: "edit-resources",
      icon: <FolderOutlined />,
      label: "Edit resources",
      allowed: hasResources,
      onClick: editResources,
    },
    {
      key: "edit-story",
      icon: <BookOutlined />,
      label: "Edit Story",
      allowed: hasStories,
      onClick: editStories,
    },
    {
      key: "edit-1wayteacher",
      icon: <TeamOutlined />,
      label: "Edit 1WayTeacher (videos)",
      allowed: has1Wayteacher,
      onClick: edit1Wayteacher,
    },
    {
      key: "edit-ap",
      icon: <TrophyOutlined />,
      label: "Edit AP",
      allowed: hasAP,
      onClick: editAP,
    },
    {
      key: "edit-ap-exams",
      icon: <FileTextOutlined />,
      label: "Edit AP exams",
      allowed: hasApExams,
      onClick: editApExams,
    },
    {
      key: "edit-exams",
      icon: <FileTextOutlined />,
      label: "Edit Exams / Standard Exam",
      allowed: hasExams,
      onClick: editExam,
    },
    {
      key: "how-to-videos",
      icon: <PlayCircleOutlined />,
      label: "How To Videos",
      allowed: hasHowToVideos,
      onClick: hotToVideos,
    },
    {
      key: "sentence-starter",
      icon: <EditOutlined />,
      label: "Sentence Starter",
      allowed: hasSentenceStarter,
      onClick: editSentenceStarter,
    },
    {
      key: "rubric",
      icon: <CheckSquareOutlined />,
      label: "Rubric",
      allowed: hasRubric,
      onClick: rubricHandler,
    },
  ];

  return (
    <SentryWrap>
      <Containermain>
        <Modal open={bookCreateLoader}>
          <VideoContainer>
            <CotationLoader
              cotation={cotationRef.current}
              messages={"Please wait while the book is being created."}
            />
          </VideoContainer>
        </Modal>

        <PageHeader>
          <Title level={2} style={{ margin: 0, fontWeight: 700, fontSize: 24 }}>
            Edit Books
          </Title>
        </PageHeader>

        <MenuBar>
          <Space wrap size="small">
            {menuButtons.map(({ key, icon, label, allowed, onClick, type, style }) => (
              <Button
                key={key}
                type={type}
                icon={icon}
                onClick={() => {
                  if (!allowed) {
                    message.warning(permissionWarningMessage);
                    return;
                  }
                  onClick();
                }}
                style={style}
              >
                {label}
              </Button>
            ))}
          </Space>
        </MenuBar>

        <TableSection>
          <CenterItem>
            <BooksTable />
          </CenterItem>
        </TableSection>
      </Containermain>
    </SentryWrap>
  );
};

export default EditBooksList;

